<template>
    <div class="relative">
		<div class="fixed white" style="width: 100%;" id="tool-bar">
			<v-row class="ma-0" style="width: 100%;" align="center" justify="start">
		        <v-col cols="auto" class="d-flex flex-column justify-center mt-3">
					<slot name="breadcrumbs" :item="true"></slot>
				</v-col>
			</v-row>
			<v-layout
				class="mx-3 mb-3 border rounded-lg d-flex flex-row wrap"
				:class="$emerald.ifLang('es', 'justify-lg-start justify-md-end', null)"
				align="center"
			>
				<v-col cols="auto" :lg="$emerald.ifLang(['ar', 'es'], ['3', '2'], '4')" class="d-flex flex-column pe-lg-0">
                    <bee-bond-up-down
                        v-model="bondNo"
                        @keydown-enter-prevent="fetchManufacturingBond(null)"
                        @programmatically-changed="fetchManufacturingBond(null)"
                        :items="manufacturingBonds.map(c => c.no)"
                        class="medium-placeholder"
                        :label="$t('manufacturingBond.bond-no')"
                    ></bee-bond-up-down>
                </v-col>
                
                <!-- add button -->
                <v-col sm="auto" cols="12" class="d-flex flex-column px-lg-0">
                    <bee-btn  
                        @click="addEntryDialog = true"
                        :content="$t('manufacturingBond.add')"
                        color="addingColor"
                        context="manufacturing_bond"
                        icon="fa-plus"
                        :action-name="actions.CREATE"
                        :disabled="file_is_locked"
                    ></bee-btn>
                </v-col>

                <!-- delete button -->
                <v-col v-if="!manufacturingBond.is_posted" sm="auto" cols="12" class="d-flex flex-column px-lg-0">
                    <bee-btn
                        :content="$t('manufacturingBond.delete')"
                        color="deletingColor"
                        icon="fa-trash-alt"
                        outer-class="ms-2"
                        :disabled="file_is_locked"
                        @click="deleteBondDialog = true"
                        context="manufacturing_bond"
                        :action-name="actions.DELETE"
                    ></bee-btn>
                </v-col>

                <!-- document date -->
                <v-col sm="auto" cols="12" class="d-flex flex-column">
                    <bee-date-picker
                        :disabled="true || manufacturingBond.is_posted || file_is_locked"
                        context="manufacturing_bond"
                        :action-name="actions.EDIT"
                        @blur="'update()'"
                        :label="$t('manufacturingBond.bond-date')"
                        v-model="bondDate"
                        :rules="rules.date"
                        :hide-details="true"
                    ></bee-date-picker>
                </v-col>

                <v-spacer/>
                        
                <!-- add product -->
                <v-col v-if="!manufacturingBond.is_posted" cols="12" sm="auto" xs="auto" md="auto" lg="auto" class="d-flex flex-column">
                    <bee-btn  
                        @click="$router.push({
                            name: 'create-product',
                            query: { manufacturingFileId: $route.query.id }
                        })"
                        :content="$t('manufacturingBond.create-bond')"
                        color="addingColor"
                        context="manufacturing_bond"
                        icon="fa-plus"
                        :action-name="actions.DELETE"
                        :disabled="file_is_locked"
                    ></bee-btn>
                </v-col>
                <v-spacer/>

                <!-- section -->
                <v-col lg="auto" sm='auto' cols="12" class="d-flex flex-column py-lg-0">
                    <bee-btn
                        context="manufacturing_bond"
                        :action-name="actions.EDIT"
                        :content="manufacturingBond.is_posted
                            ? $t('manufacturingBond.edit')
                            : $t('manufacturingBond.save')
                        " 
                        :icon="manufacturingBond.is_posted ? 'fa-envelope-open' : 'fa-envelope'" 
                        :outer-class="$vuetify.breakpoint.lgAndUp ? 'py-8': ''"
                        :disabled="file_is_locked"
                        :color="manufacturingBond.is_posted ? 'deletingColor' : 'blueGray'"
                        @clic="manufacturingBond.is_posted =! manufacturingBond.is_posted"
                        @click="manufacturingBond.is_posted? unPost(): post()"
                    >
                    </bee-btn>
                </v-col>
                <v-col lg="1" md="1" cols="12" class="d-flex flex-column"
                    :class="[
                        manufacturingBond.is_posted ?'postedColor':'unpostedColor',
                        $vuetify.breakpoint.mdAndUp ? 'border-round-end':'border-round-bottom'
                    ]"
                >
                    <span class="d-flex justify-center text-center align-center fill-height text-wrap white--text font-weight-bold" style="line-height: 1.2;">
                        {{manufacturingBond.is_posted ? '' : $t('manufacturingBond.not')}}
                        <br v-if="!manufacturingBond.is_posted && $vuetify.breakpoint.mdAndUp">
                        {{$t('manufacturingBond.posted')}}
                    </span>
                </v-col>
            </v-layout>
        </div>

        <div class="ma-auto absolute" id="main" style="width: 100%; height: auto">
            <v-card flat>
                <v-card-text class="pt-0 px-3">
                    <bee-simple-table
                        ref="table"
                        :with-color="false"
						:zebra="true"
						:hide-footer="true"
						empty-state="dashes"
						context="manufacturing_bond"
						:fixed-header="true"
						:action-name="actions.GET"
						:headers="headers"
                        :configs="configs"
                        :items="entries || []"
                        :show-menu-actions="true"
                        :right-click-menu-items="rightClickMenuItemsFiltered"
                        @context-menu-show-details="showDetails($event)"
                        @context-menu-edit="editRawMaterials($event)"
                        @context-menu-delete="confirmDeleteEntry($event)"
                    >
                        <template v-slot:[`item.warehouse_id`]="{item}">
                            {{
                                warehouses
                                .find(c => c.id === materials.find(c => c.id === item.material_id).warehouse_id)
                                .name
                            }}
                        </template>
                    </bee-simple-table>
                </v-card-text>
            </v-card>
        </div>

        <!-- fixed footer -->
        <v-bottom-navigation id="footer" app class="px-3" horizontal background-color="#E1E2E4">
            <v-row align="center" justify="center" justify-lg="end">
                <!-- balance -->
                <v-col cols="auto" lg="auto" class="ma-0 pa-0 d-flex">
                    <bee-outlined-header-info
                        border-color="transparent"
                        :head="$t('manufacturingBond.total-costs')"
                        :column="false"
                    >
                        <template  v-slot:content="{}">
                            <bee-money-label
                                :number="summaryBalance"
                                empty-state="_"
                            ></bee-money-label>
                        </template>
                    </bee-outlined-header-info>
                </v-col>
            </v-row>
        </v-bottom-navigation>

        <!-- Add bond dialog -->
        <v-dialog v-model="addEntryDialog" max-width="500" transition="dialog-top-transition">
            <v-card>
                <v-card-title class="headline">
                    {{$t('manufacturingBond.create-manufacturing-bond')}}
                </v-card-title>
                <v-card-text>
                    <bee-date-picker
                      v-model="date"
                      :label="$t('manufacturingBond.manufacturing-bond-date')"
                      context="manufacturing_bond"
                      :action-name="actions.CREATE"
                      :rules="rules.date"
                    ></bee-date-picker>
                </v-card-text>
                <v-card-actions class="ms-3">
                    <v-btn color="greenDarken1" text @click="createManufacturingBond" :loading="loading" >
                        {{$t('manufacturingBond.create')}}
                    </v-btn>
                    <v-btn color="redDarken1" text @click="addEntryDialog = false" >
                        {{$t('manufacturingBond.cancel')}}
                    </v-btn>
                    <v-spacer></v-spacer>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- delete entry dialog -->
        <bee-dialog
            v-model="deleteEntryDialog"
            :title="$t('manufacturingBond.delete-entry')"
            :message="$t('manufacturingBond.are-you-sure-you-want-to-delete-the-entry')"
            @handler="deleteEntry();"
            @close="deleteEntryDialog = false"
        ></bee-dialog>
        
        <!-- delete bond dialog -->
        <bee-dialog
            v-model="deleteBondDialog"
            :title="$t('manufacturingBond.delete-journal')"
            :message="$t('manufacturingBond.are-you-sure-you-want-to-delete-the-journal')"
            @handler="deleteManufacturingBond()"
            @close="deleteBondDialog = false"
        ></bee-dialog>
    </div>
</template>

<script>
import actions from "../../../permissions/actions-names";
import store from "../../../store/index";
import rules from "../../../validation";
import { mapState, mapGetters } from 'vuex';
export default {
    name: 'manufacturing-bond',
    
    data(){
        return {
            bondNo: null,
            bondDate: null,

            date: null,

            addEntryDialog: false,
            deleteEntryDialog: false,
            deleteBondDialog: false,

            deleteEntryId: null,
            
            loading: false,
            actions,
            rules,
        }
    },

    computed: {
        ...mapState({
            configs:  state => state.configs.configs,
            materials:  state => state.materials.materials,
            warehouses:  state => state.warehouses.warehouses,
            manufacturingBonds:  state => state.manufacturing.manufacturingBonds,
            manufacturingBond:  state => state.manufacturing.manufacturingBond,
            entries:  state => state.manufacturing.entries,
            unitSizes: state => state.unitSizes.unit_sizes,
        }),

        ...mapGetters({
            getBondById: 'manufacturing/getBondById',
            getBondByNo: 'manufacturing/getBondByNo',
        }),

        rightClickMenuItems() {
            return [
                {
                    name: 'context-menu-show-details',  
                    disabled: false,
                    action_name: actions.EDIT,
                    text: this.$t('manufacturingBond.context-menu.show-details')
                },
                {
                    name: 'context-menu-edit',  
                    disabled: false,
                    action_name: actions.EDIT,
                    text: this.$t('manufacturingBond.context-menu.edit')
                },
                {
                    name: 'context-menu-delete',  
                    disabled: false,
                    action_name: actions.DELETE,
                    text: this.$t('manufacturingBond.context-menu.delete')
                },
            ]
        },

        rightClickMenuItemsFiltered(){
            if (!this.manufacturingBond.is_posted){
                return this.rightClickMenuItems.filter(c => c.name !== 'context-menu-show-details')
            }
            else {
                return this.rightClickMenuItems.filter(c => c.name === 'context-menu-show-details')
            }
        },

        headers() {
            return [
                {
                    text: this.$t('manufacturingBond.headers.no'),
                    value: 'no',
                    type: 'auto_increment',
                    width: '8%',
                },
                {
                    text: this.$t('manufacturingBond.headers.warehouse'),
                    value: 'warehouse_id',
                    width: '15%',
                    editable: false
                },
                {
                    text: this.$t('manufacturingBond.headers.material-name'),
                    value: 'material_id',
                    // type: 'materials-autocomplete',
                    type: 'autocomplete',
                    width: '10%',
                    editable: true,
                    settings: {
                        items: this.materials.filter(c => c.type == 2),
                        
                        itemText: 'name',
                        itemValue: 'id',
                        context: 'material',
                        actionName: this.actions.GET
                    }
                },
                {
                    text: this.$t('manufacturingBond.headers.count'),
                    value: 'count',
                    type: 'numeric',
                    width: '10%',
                    editable: true,
                    settings: { itemText: 'name' }
                },
                {
                    text: this.$t('manufacturingBond.headers.produced-quantity'),
                    value: 'quantity.quantity',
                    type: 'numeric',
                    width: '10%',
                    editable: true,
                    settings: { itemText: 'name' }
                },
                {
                    text: this.$t('manufacturingBond.headers.unit'),
                    value: 'unit_size_id',
                    type: 'autocomplete',
                    width: '10%',
                    editable: true,
                    settings: {
                        items: this.unitSizes,
                        itemText: 'name',
                        itemValue: 'id'
                    }
                },
                {
                    text: this.$t('manufacturingBond.headers.loss-ratio') + '%',
                    value: 'loss_ratio',
                    type: 'numeric',
                    width: '10%',
                    editable: true,
                    settings: { itemText: 'name' }
                },
                {
                    text: this.$t('manufacturingBond.headers.cost-of-one'),
                    value: 'cost',
                    type: 'money',
                    width: '10%',
                    editable: true,
                    settings: { itemText: 'name' }
                },
                {
                    text: this.$t('manufacturingBond.headers.total-cost'),
                    value: 'total_cost',
                    type: 'money',
                    width: '13%',
                    editable: true,
                    settings: { itemText: 'name' }
                },
            ]
        },

        summaryBalance(){
            return  this.entries.length > 0
                ? this.entries.map(c => c.total_cost.amount).reduce((a, b) => a + b)
                : null;
        },

        file_is_locked(){
            return this.configs.financial_file.is_locked;
        },
    },

    methods:{
        // manufacturing bond
        fetchManufacturingBonds(){
            return this.$store.dispatch('manufacturing/fetchAll', {
                fromDate: this.configs.financial_file.start_date,
                toDate: new Date ().toISOString().substring(0, 10),
                postingStatus: 3 // 3 => all bonds
            })
        },

        fetchManufacturingBond(bondId){
            let id;
            if (bondId){
                id = bondId;
            }
            else {
                id = this.getBondByNo(this.bondNo)?.id;
            }
            
            if (id) {
                this.$store.dispatch('manufacturing/fetchById', { manufacturingFileId: id })
                .then(() => {
                    this.bondNo = this.manufacturingBond.no;
                    this.bondDate = this.manufacturingBond.date;

                    this.fetchManufacturingBondEntries(id);
                })
                .then(() => {
                    if(!bondId){
                        this.$router.replace({ name: 'manufacturing-bond', query: { id } })
                    }
                })
            }
        },

        createManufacturingBond(){
            this.$store.dispatch('manufacturing/create', {
                date: this.date
            })
            .then((data) => {
                this.fetchManufacturingBonds()
                .then(() => {
                    this.$router.replace({ name: 'manufacturing-bond', query: { id: data } })
                    .then((route) => {
                        this.fetchManufacturingBond(route.query.id);
                    })
                })
                this.addEntryDialog= false;
            })
        },

        deleteManufacturingBond(){
            this.$store.dispatch('manufacturing/delete', {
                manufacturingFileId: this.$route.query.id
            })
            .then(() => {
                this.fetchManufacturingBonds()
                .then(() => {
                    this.$router.replace({
                        name: 'manufacturing-bond',
                        query: { id: this.manufacturingBonds[this.manufacturingBonds.length - 1].id }
                    })
                    .then((route) => {
                        this.fetchManufacturingBond(route.query.id);
                    })
                })
            })
        },

        post(){
            this.$store.dispatch('manufacturing/updateToPosted', {
                manufacturingFileId: this.$route.query.id
            })
            .then(() => {
                this.fetchManufacturingBond(this.$route.query.id);
                this.$store.dispatch('materials/fetchAll')
            })
        },
        unPost(){
            this.$store.dispatch('manufacturing/updateToNotPosted', {
                manufacturingFileId: this.$route.query.id
            })
            .then(() => {
                this.fetchManufacturingBond(this.$route.query.id);
                this.$store.dispatch('materials/fetchAll')
            })
        },

        // manufacturing entries
        fetchManufacturingBondEntries(bondId){
            this.$refs.table.openLoading();
            this.$store.dispatch('manufacturing/fetchAllEntries', {
                manufacturingFileId: bondId
            })
            .finally(() => {
                this.$refs.table.closeLoading();
            })
        },

        confirmDeleteEntry(entry){
            this.deleteEntryDialog = true;
            this.deleteEntryId = entry.id;
        },

        deleteEntry(){
            this.$store.dispatch('manufacturing/deleteEntry', {
                manufacturingFileId: this.$route.query.id,
                entryId: this.deleteEntryId,
            })
            .then(() => {
                this.fetchManufacturingBondEntries(this.$route.query.id);
            })
        },

        // handlers
        showDetails(entry){
            this.$router.push({name: 'show-product', query: {
                manufacturingFileId: this.$route.query.id,
                entryId: entry.id,
            }})
        },

        editRawMaterials(e, i){
            let entry;
            // if i has value so entry came from doubleclick
            if (i){
                entry = i.item;
            }
            // else it came from context menu
            else {
                entry = e
            }
            this.$router.push({name: 'edit-product', query: {
                manufacturingFileId: this.$route.query.id,
                entryId: entry.id
            }})
        },
    },
    
    async beforeRouteEnter(to, from, next) {
        let promises = [];

        let materials = store.state.materials.materials;
        if(Object.keys(materials).length === 0){
            promises.push(store.dispatch("materials/fetchAll"));
        }

        let warehouses = store.state.warehouses.warehouses;
        if(Object.keys(warehouses).length === 0){
            promises.push(store.dispatch("warehouses/fetchAll"));
        }

        let unitSizes = store.state.unitSizes.unit_sizes;
        if(Object.keys(unitSizes).length === 0){
            promises.push(store.dispatch("unitSizes/fetchAll"));
        }

        await Promise.all(promises);

        next((vm) => {
            store.dispatch('manufacturing/fetchAll', {
                fromDate: store.state.configs.configs.financial_file.start_date,
                toDate: new Date ().toISOString().substring(0, 10),
                postingStatus: 3 // 3 => all bonds
            })
            .then(() => {
                if (vm.$route.query.id){
                    vm.fetchManufacturingBond(vm.$route.query.id);
                }
                else {
                    const lastManufacturingBondId =
                    vm.manufacturingBonds[vm.manufacturingBonds.length -1].id;
                    vm.$router.replace({ name: 'manufacturing-bond', query: { id: lastManufacturingBondId } })
                    .then(() => {
                        vm.fetchManufacturingBond(lastManufacturingBondId);
                    })
                }
            })
        });
    },

    mounted(){
        window.observer.observe(document.querySelector("#tool-bar"));
        window.observer.observe(document.querySelector("#footer"));
    }
}
</script>

<style scoped>
.border {
	border: 1px solid #f1f1f1;
    border-radius: 8px;
}
</style>